// src/components/admin/nornsweave/CalendarTab.js
import React, { useState } from 'react';
import { useAdminNornsWeave, getLastName, getEventZIndex } from './AdminNornsWeaveContext';
import { formatTime12 } from '../../TimePicker';

function CalendarTab({
  // Navigation helpers
  canNavigatePrevious,
  canNavigateNext,
  getWeekLabel,
  // Data access helpers
  getLibraryHours,
  getLibraryHoursForDate,
  getLibraryHourSlots,
  getSaturdayHours,
  getAllEventsForDay,
  getFullDayClosureInfo,
  getEventPosition,
  getEventColor,
  hasAideCoverage,
  // Actions
  onAddEvent,
  onAddClosure,
  onAddOverride,
  onAddSingleClass,
  onEditBooking,
  onDeletePersonalEvent,
  onDeleteClosure,
  onDeleteSchoolCalendarEvent
}) {
  const {
    weekOffset,
    setWeekOffset,
    activeLibrary,
    setActiveLibrary,
    weekDates,
    weekHasOpenSaturday,
    getSaturdayDescription
  } = useAdminNornsWeave();

  const hasSaturday = weekHasOpenSaturday(weekOffset);
  const dayNames = hasSaturday
    ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const [showLegend, setShowLegend] = useState(false);

  return (
    <div>
      {/* Action Buttons */}
      <div className="flex justify-center items-center gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setShowLegend(!showLegend)}
          className={`px-4 py-2 rounded-lg transition-colors border-2 ${
            showLegend
              ? 'bg-slate-100 border-slate-300 text-slate-700'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          {showLegend ? 'Hide Legend' : 'Show Legend'}
        </button>
        <button
          onClick={onAddSingleClass}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Single Class
        </button>
        <button
          onClick={onAddOverride}
          className="bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 transition-colors"
        >
          + Schedule Override
        </button>
        <button
          onClick={onAddEvent}
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
        >
          + Library Event
        </button>
        <button
          onClick={onAddClosure}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Close Library
        </button>
      </div>

      {/* Collapsible Legend */}
      {showLegend && (
        <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
          <div className="flex gap-4 text-sm flex-wrap justify-center">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span>Independent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-500 rounded"></div>
              <span>Needs Librarian</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-cyan-500 rounded"></div>
              <span>Fixed Class</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-500 rounded"></div>
              <span>Your Schedule</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <span>Library Event</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-600 rounded"></div>
              <span>Holiday/Closed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-violet-600 rounded"></div>
              <span>Holy Day/Mass</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-violet-400 rounded"></div>
              <span>Adoration</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
              <span>Aide Coverage</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-violet-100 border border-violet-300 rounded"></div>
              <span>Aide (Mass Day)</span>
            </div>
          </div>
        </div>
      )}

      {/* Week Navigation */}
      <div className="flex justify-center items-center gap-4 mb-4">
        <button
          onClick={() => canNavigatePrevious() && setWeekOffset(prev => prev - 1)}
          disabled={!canNavigatePrevious()}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            canNavigatePrevious()
              ? 'bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50'
              : 'bg-slate-100 border-2 border-slate-100 text-slate-400 cursor-not-allowed'
          }`}
        >
          ← Previous
        </button>
        <div className="bg-slate-100 px-6 py-2 rounded-lg">
          <span className="font-bold text-slate-800">{getWeekLabel()}</span>
          <span className="text-slate-600 text-sm ml-2">
            ({weekDates[0]} - {weekDates[weekDates.length - 1]})
          </span>
        </div>
        <button
          onClick={() => canNavigateNext() && setWeekOffset(prev => prev + 1)}
          disabled={!canNavigateNext()}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            canNavigateNext()
              ? 'bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50'
              : 'bg-slate-100 border-2 border-slate-100 text-slate-400 cursor-not-allowed'
          }`}
        >
          Next →
        </button>
        {weekOffset !== 0 && (
          <button
            onClick={() => setWeekOffset(0)}
            className="text-blue-600 hover:text-blue-800 text-sm underline"
          >
            Today
          </button>
        )}
      </div>

      {/* Library Tabs */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setActiveLibrary('elementary')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              activeLibrary === 'elementary'
                ? 'bg-purple-600 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-200'
            }`}
          >
            Elementary Library
          </button>
          <button
            onClick={() => setActiveLibrary('middle')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              activeLibrary === 'middle'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-200'
            }`}
          >
            Middle School Library
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {(() => {
          const library = activeLibrary;
          const hours = getLibraryHours(library);
          const hourSlots = getLibraryHourSlots(library);
          const hourHeight = 60;
          const totalHours = hourSlots.length - 1;
          const calendarHeight = totalHours * hourHeight;
          const isElem = library === 'elementary';

          return (
            <div className="flex">
              {/* Time column */}
              <div className={`flex-shrink-0 w-20 border-r ${isElem ? 'bg-purple-50 border-purple-200' : 'bg-indigo-50 border-indigo-200'}`}>
                <div className={`h-14 border-b ${isElem ? 'bg-purple-100 border-purple-200' : 'bg-indigo-100 border-indigo-200'}`} />
                {hourSlots.slice(0, -1).map((slot) => (
                  <div
                    key={slot}
                    className={`border-b text-sm text-slate-600 font-medium pr-3 flex items-start justify-end pt-1 ${isElem ? 'border-purple-100' : 'border-indigo-100'}`}
                    style={{ height: `${hourHeight}px` }}
                  >
                    {formatTime12(slot)}
                  </div>
                ))}
              </div>

              {/* Day columns */}
              <div className="flex-1 flex">
                {weekDates.map((date, dayIdx) => {
                  const dayEvents = getAllEventsForDay(library, date);
                  const dayName = dayNames[dayIdx];
                  const fullDayInfo = getFullDayClosureInfo(library, date);
                  // Get hours for this specific date (Saturday may have different hours)
                  const dayHours = getLibraryHoursForDate ? getLibraryHoursForDate(library, date) : hours;

                  // Get Saturday description if it's a Saturday
                  const saturdayDesc = dayName === 'Saturday' ? getSaturdayDescription(date) : '';

                  return (
                    <div key={date} className={`flex-1 border-r last:border-r-0 ${isElem ? 'border-purple-100' : 'border-indigo-100'}`}>
                      {/* Day header */}
                      <div className={`${saturdayDesc ? 'h-16' : 'h-14'} border-b flex flex-col items-center justify-center ${
                        dayName === 'Saturday'
                          ? 'bg-amber-100 border-amber-200'
                          : isElem ? 'bg-purple-100 border-purple-200' : 'bg-indigo-100 border-indigo-200'
                      }`}>
                        <div className={`text-sm font-bold ${dayName === 'Saturday' ? 'text-amber-800' : 'text-slate-800'}`}>{dayName}</div>
                        <div className={`text-xs ${dayName === 'Saturday' ? 'text-amber-600' : 'text-slate-500'}`}>{date}</div>
                        {saturdayDesc && (
                          <div className="text-[10px] text-amber-700 font-medium truncate max-w-full px-1" title={saturdayDesc}>
                            {saturdayDesc}
                          </div>
                        )}
                      </div>

                      {/* Day content container */}
                      <div className="relative" style={{ height: `${calendarHeight}px`, overflow: 'visible' }}>
                        {/* Grid lines (background) */}
                        <div className="absolute inset-0" style={{ zIndex: 0 }}>
                          {hourSlots.slice(0, -1).map((slot, idx) => {
                            const nextSlot = hourSlots[idx + 1] || slot;
                            // Skip aide coverage shading on Saturdays (no aides work on Saturdays)
                            const aideCoverage = dayName !== 'Saturday'
                              ? hasAideCoverage(library, date, slot, nextSlot)
                              : { hasCoverage: false };
                            const bgClass = aideCoverage.hasCoverage
                              ? (aideCoverage.isMassDay ? 'bg-violet-50' : 'bg-green-50')
                              : '';
                            return (
                              <div
                                key={slot}
                                className={`border-b ${isElem ? 'border-purple-100' : 'border-indigo-100'} ${bgClass}`}
                                style={{ height: `${hourHeight}px` }}
                              />
                            );
                          })}
                        </div>

                        {/* Aide coverage tooltip layer (on top for hover) - skip on Saturdays */}
                        {dayName !== 'Saturday' && (
                          <div className="absolute inset-0" style={{ zIndex: 50, pointerEvents: 'none' }}>
                            {hourSlots.slice(0, -1).map((slot, idx) => {
                              const nextSlot = hourSlots[idx + 1] || slot;
                              const aideCoverage = hasAideCoverage(library, date, slot, nextSlot);
                              if (!aideCoverage.aideNames || aideCoverage.aideNames.length === 0) return null;
                              const tooltipText = `Aides: ${aideCoverage.aideNames.join(', ')}${aideCoverage.isMassDay ? ' (Mass Day Schedule)' : ''}`;
                              return (
                                <div
                                  key={`tooltip-${slot}`}
                                  className="cursor-help"
                                  style={{
                                    position: 'absolute',
                                    top: `${idx * hourHeight}px`,
                                    left: 0,
                                    right: 0,
                                    height: `${hourHeight}px`,
                                    pointerEvents: 'auto'
                                  }}
                                  title={tooltipText}
                                />
                              );
                            })}
                          </div>
                        )}

                        {/* Full-day closure overlay */}
                        {fullDayInfo.isClosed && (
                          <div
                            className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                            style={{
                              zIndex: 20,
                              backgroundColor: 'rgba(239, 68, 68, 0.85)',
                              pointerEvents: 'auto'
                            }}
                            onClick={() => {
                              if (fullDayInfo.type === 'closure' && fullDayInfo.data?.id) {
                                if (window.confirm(`Delete this closure (${fullDayInfo.label})?`)) {
                                  onDeleteClosure(fullDayInfo.data.id);
                                }
                              } else if (fullDayInfo.type === 'holiday' && fullDayInfo.data?.id) {
                                if (window.confirm(`Delete this holiday (${fullDayInfo.label})?`)) {
                                  onDeleteSchoolCalendarEvent(fullDayInfo.data.id);
                                }
                              }
                            }}
                          >
                            <div className="text-white text-center px-2">
                              <div className="text-3xl mb-2">🚫</div>
                              <div className="font-bold text-sm">{fullDayInfo.label}</div>
                              <div className="text-xs opacity-80 mt-1">Library Closed</div>
                            </div>
                          </div>
                        )}

                        {/* Workday overlay */}
                        {fullDayInfo.isWorkday && !fullDayInfo.isClosed && (
                          <div
                            className="absolute inset-0 flex flex-col"
                            style={{
                              zIndex: 5,
                              backgroundColor: 'rgba(148, 163, 184, 0.25)',
                              pointerEvents: 'none'
                            }}
                          >
                            <div
                              className="flex items-center justify-center shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                              style={{
                                backgroundColor: 'rgba(100, 116, 139, 0.95)',
                                height: '36px',
                                pointerEvents: 'auto'
                              }}
                              onClick={() => {
                                if (fullDayInfo.data?.id && window.confirm(`Delete ${fullDayInfo.label}?`)) {
                                  onDeleteSchoolCalendarEvent(fullDayInfo.data.id);
                                }
                              }}
                            >
                              <div className="text-white text-center text-xs font-medium">
                                <div>{fullDayInfo.label}</div>
                                <div className="text-[10px] opacity-80">No Classes - Bookings OK</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* All-day Library Event overlay (yellow/gold background) */}
                        {fullDayInfo.isLibraryEvent && !fullDayInfo.isClosed && !fullDayInfo.isWorkday && (
                          <div
                            className="absolute inset-0 flex flex-col"
                            style={{
                              zIndex: 5,
                              backgroundColor: 'rgba(250, 204, 21, 0.20)',
                              pointerEvents: 'none'
                            }}
                          >
                            <div
                              className="flex items-center justify-center shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                              style={{
                                backgroundColor: 'rgba(202, 138, 4, 0.95)',
                                height: '36px',
                                pointerEvents: 'auto'
                              }}
                              onClick={() => {
                                if (fullDayInfo.data?.id && window.confirm(`Delete this library event (${fullDayInfo.label})?`)) {
                                  onDeletePersonalEvent(fullDayInfo.data.id);
                                }
                              }}
                            >
                              <div className="text-white text-center text-xs font-medium">
                                <div>{fullDayInfo.label}</div>
                                <div className="text-[10px] opacity-80">Library Event</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* No Bookings override overlay (violet/purple background) */}
                        {fullDayInfo.isNoBookings && !fullDayInfo.isClosed && !fullDayInfo.isWorkday && (
                          <div
                            className="absolute inset-0 flex flex-col"
                            style={{
                              zIndex: 5,
                              backgroundColor: 'rgba(139, 92, 246, 0.15)',
                              pointerEvents: 'none'
                            }}
                          >
                            <div
                              className="flex items-center justify-center shrink-0"
                              style={{
                                backgroundColor: 'rgba(124, 58, 237, 0.95)',
                                height: '36px',
                                pointerEvents: 'auto'
                              }}
                            >
                              <div className="text-white text-center text-xs font-medium">
                                <div>{fullDayInfo.label}</div>
                                <div className="text-[10px] opacity-80">Admin Only</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* No Classes override overlay (teal background - bookings allowed) */}
                        {fullDayInfo.isNoClasses && !fullDayInfo.isClosed && !fullDayInfo.isWorkday && (
                          <div
                            className="absolute inset-0 flex flex-col"
                            style={{
                              zIndex: 5,
                              backgroundColor: 'rgba(20, 184, 166, 0.15)',
                              pointerEvents: 'none'
                            }}
                          >
                            <div
                              className="flex items-center justify-center shrink-0"
                              style={{
                                backgroundColor: 'rgba(13, 148, 136, 0.95)',
                                height: '36px',
                                pointerEvents: 'auto'
                              }}
                            >
                              <div className="text-white text-center text-xs font-medium">
                                <div>{fullDayInfo.label}</div>
                                <div className="text-[10px] opacity-80">Bookings OK</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Inventory period overlay (amber/orange background - admin bookings only) */}
                        {fullDayInfo.isInventory && !fullDayInfo.isClosed && (
                          <div
                            className="absolute inset-0 flex flex-col"
                            style={{
                              zIndex: 5,
                              backgroundColor: 'rgba(245, 158, 11, 0.15)',
                              pointerEvents: 'none'
                            }}
                          >
                            <div
                              className="flex items-center justify-center shrink-0"
                              style={{
                                backgroundColor: 'rgba(217, 119, 6, 0.95)',
                                height: '36px',
                                pointerEvents: 'auto'
                              }}
                            >
                              <div className="text-white text-center text-xs font-medium">
                                <div>{fullDayInfo.label}</div>
                                <div className="text-[10px] opacity-80">Admin Bookings Only</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Saturday blocked time overlays - show blocked time before/after Saturday hours */}
                        {dayName === 'Saturday' && !fullDayInfo.isClosed && (() => {
                          const satHours = getSaturdayHours();
                          // Get the regular hours being displayed (the grid hours)
                          const gridHours = hours;

                          // Calculate blocked regions as percentages of the grid
                          const timeToMinutes = (time) => {
                            const [h, m] = time.split(':').map(Number);
                            return h * 60 + m;
                          };

                          const gridStartMin = timeToMinutes(gridHours.start);
                          const gridEndMin = timeToMinutes(gridHours.end);
                          const gridTotalMin = gridEndMin - gridStartMin;

                          const satStartMin = timeToMinutes(satHours.start);
                          const satEndMin = timeToMinutes(satHours.end);

                          // Before Saturday open time (top block)
                          const beforeBlockHeight = satStartMin > gridStartMin
                            ? ((satStartMin - gridStartMin) / gridTotalMin) * 100
                            : 0;

                          // After Saturday close time (bottom block)
                          const afterBlockTop = satEndMin < gridEndMin
                            ? ((satEndMin - gridStartMin) / gridTotalMin) * 100
                            : 100;
                          const afterBlockHeight = 100 - afterBlockTop;

                          return (
                            <>
                              {/* Block before Saturday opens */}
                              {beforeBlockHeight > 0 && (
                                <div
                                  className="absolute left-0 right-0 flex items-center justify-center"
                                  style={{
                                    top: 0,
                                    height: `${beforeBlockHeight}%`,
                                    backgroundColor: 'rgba(148, 163, 184, 0.75)',
                                    zIndex: 15,
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <div className="text-slate-600 text-xs font-medium text-center">
                                    <div>Library Closed</div>
                                    <div className="text-[10px] opacity-70">Opens at {formatTime12(satHours.start)}</div>
                                  </div>
                                </div>
                              )}
                              {/* Block after Saturday closes */}
                              {afterBlockHeight > 0 && (
                                <div
                                  className="absolute left-0 right-0 flex items-center justify-center"
                                  style={{
                                    top: `${afterBlockTop}%`,
                                    height: `${afterBlockHeight}%`,
                                    backgroundColor: 'rgba(148, 163, 184, 0.75)',
                                    zIndex: 15,
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <div className="text-slate-600 text-xs font-medium text-center">
                                    <div>Library Closed</div>
                                    <div className="text-[10px] opacity-70">Closed at {formatTime12(satHours.end)}</div>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}

                        {/* Events overlay */}
                        {!fullDayInfo.isClosed && (
                          <div
                            className="absolute inset-0"
                            style={{ zIndex: 10, pointerEvents: 'none', overflow: 'visible' }}
                          >
                            {dayEvents
                              .filter(event => !(event.eventType === 'personal' && event.isAllDay))
                              .map((event, idx) => {
                              const pos = getEventPosition(event.startTime, event.endTime, dayHours.start, dayHours.end);
                              const eventKey = event.id || `${event.eventType}-${event.startTime}-${event.endTime}-${event.day || ''}-${idx}`;

                              return (
                                <div
                                  key={eventKey}
                                  className="absolute rounded-lg text-white cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                                  style={{
                                    top: `${pos.top}%`,
                                    height: `${pos.height}%`,
                                    minHeight: '32px',
                                    left: '4px',
                                    right: '4px',
                                    zIndex: getEventZIndex(event),
                                    backgroundColor: getEventColor(event),
                                    pointerEvents: 'auto',
                                    overflow: 'hidden'
                                  }}
                                  onClick={() => {
                                    if (event.eventType === 'booking') {
                                      onEditBooking(event);
                                    } else if (event.eventType === 'personal') {
                                      if (window.confirm('Delete this library event?')) {
                                        onDeletePersonalEvent(event.id);
                                      }
                                    } else if (event.eventType === 'closure') {
                                      if (window.confirm(`Delete this closure (${event.displayLabel})?`)) {
                                        onDeleteClosure(event.id);
                                      }
                                    }
                                  }}
                                >
                                  <div className="px-1 py-0.5 h-full flex flex-col">
                                    <div className="font-semibold text-[10px] leading-tight truncate">
                                      {formatTime12(event.startTime)} - {formatTime12(event.endTime)}
                                    </div>
                                    <div className="text-[10px] opacity-90 leading-tight truncate">
                                      {event.eventType === 'booking'
                                        ? `${event.grade || event.type} - ${getLastName(event.teacherName)}`
                                        : event.eventType === 'fixedClass'
                                        ? `${event.grade}${event.subject ? ' ' + event.subject : ''} - ${getLastName(event.teacherName)}`
                                        : event.eventType === 'block'
                                        ? (event.reason || event.title || 'Block')
                                        : event.eventType === 'closure' ? event.displayLabel
                                        : event.eventType === 'holyDay' ? event.displayLabel
                                        : event.eventType === 'adoration' ? event.displayLabel
                                        : event.eventType === 'holiday' ? event.displayLabel
                                        : event.eventType === 'halfDay' ? event.displayLabel
                                        : event.eventType === 'halfDayBanner' ? event.displayLabel
                                        : event.eventType === 'libraryEvent' ? event.displayLabel
                                        : event.eventType === 'massBlock' ? event.displayLabel
                                        : event.eventType === 'mass' ? event.displayLabel
                                        : event.title || 'Event'}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default CalendarTab;
